import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import FeaturedGenres from './components/FeaturedGenres';
import FeaturedMoods from './components/FeaturedMoods';
import Loader from './components/Loader';
import RecommendationForm from './components/RecommendationForm';
import RecommendationResults from './components/RecommendationResults';
import useRecommendations from './useRecommendations';

const AIRecommendations: React.FC = () => {
  const { t } = useTranslation();

  const { search } = useLocation();

  const recommendationQuery = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('query');
  }, [search]);

  const { queryRecommendations, results } = useRecommendations();

  const onFormSearch = useCallback(
    (query: string) => {
      queryRecommendations(query);
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}?query=${query}`
      );
    },
    [queryRecommendations]
  );

  useEffect(() => {
    if (!recommendationQuery) {
      return;
    }
    queryRecommendations(recommendationQuery);
  }, [recommendationQuery, queryRecommendations]);

  const { loading, error, recommendations } = results || {};

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Loader open={loading} />
      <Box
        px={{
          xs: 4,
          sm: 10,
        }}
        py={2}
        maxWidth={1200}
        width="100%"
        sx={{
          overflowX: 'auto',
        }}
      >
        <Box mt={9}>
          <Typography
            data-testid="recommendation-form-header"
            variant="h3"
            mb={2}
          >
            {t(
              error
                ? 'aiRecommendations:form:errorHeader'
                : 'aiRecommendations:form:header'
            )}
          </Typography>
          <RecommendationForm
            defaultValue={recommendationQuery}
            onSearch={onFormSearch}
          />
        </Box>
        <RecommendationResults
          recommendations={recommendations}
          error={!!error}
        />
        <Box mt={9}>
          <FeaturedGenres />
        </Box>
        <Box mt={4}>
          <FeaturedMoods />
        </Box>
      </Box>
    </Box>
  );
};

export default AIRecommendations;
